import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGPieChart } from '../../components/primitives/PGChart';
import {
  vehiclesByBranch,
  FLEET_BRANCH,
  FLEET_GROUP,
  complianceWatchlist,
  fmtRk,
  fmtKm,
  MONTHS_FLEET,
} from '../../utils/fleetData';
import { getBranch } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const {
  FiTruck, FiActivity, FiMapPin, FiDroplet, FiTool, FiDollarSign, FiTrendingDown,
} = FiIcons;

const statusTone = (s) => ({
  Active: 'ok', Service: 'warn', Standby: 'info', Grounded: 'alert',
}[s] || 'neutral');

const utilColor = (v) => (v >= 85 ? PG.emerald : v >= 70 ? PG.amber : PG.red);

const TYPE_SHORT = {
  'fitment-truck': 'Truck',
  'service-van': 'Van',
  'supervisor-bakkie': 'Bakkie',
  'courier-bike': 'Bike',
};

const COLS = [
  { key: 'id',             label: 'Reg_ID',  align: 'left'  },
  { key: 'typeName',       label: 'Type',    align: 'left'  },
  { key: 'model',          label: 'Model',   align: 'left'  },
  { key: 'techAssigned',   label: 'Tech',    align: 'left'  },
  { key: 'status',         label: 'Status',  align: 'left'  },
  { key: 'kmThisMonth',    label: 'Km_MTD',  align: 'right' },
  { key: 'fuelCostMTD',    label: 'Fuel_R',  align: 'right' },
  { key: 'maintCostMTD',   label: 'Maint_R', align: 'right' },
  { key: 'stockSalesMTD',  label: 'Sales_R', align: 'right' },
  { key: 'utilPct',        label: 'Util%',   align: 'right' },
];

const today = Date.now();
const isPast = (iso) => new Date(iso).getTime() < today;

export default function FleetBranchView({ branchId }) {
  const branch = getBranch(branchId);
  const fleet = FLEET_BRANCH[branchId];
  const vehicles = vehiclesByBranch(branchId);

  const [selectedId, setSelectedId] = useState(vehicles[0]?.id);
  const [sortKey, setSortKey] = useState('stockSalesMTD');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!vehicles.length) return [];
    const copy = [...vehicles];
    copy.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? ''); const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [vehicles, sortKey, sortDir]);

  if (!branch || !fleet) {
    return (
      <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-[10px] uppercase tracking-[0.3em]">
        Fleet_Not_Found
      </div>
    );
  }

  const selected = vehicles.find((v) => v.id === selectedId) ?? vehicles[0];

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const healthy = fleet.grounded === 0 && fleet.activePct >= 80;
  const watch = fleet.activePct >= 70;
  const healthLabel = healthy ? 'Healthy' : watch ? 'Watch' : 'Attention';
  const healthTone = healthy ? 'ok' : watch ? 'warn' : 'alert';

  const utilAccent = fleet.avgUtilPct >= FLEET_GROUP.avgUtilPct ? PG.emerald : PG.amber;
  const revAccent = fleet.revPerVehicle >= FLEET_GROUP.revPerVehicle ? PG.emerald : PG.amber;
  const cpkAccent = fleet.costPerKm <= FLEET_GROUP.costPerKm ? PG.emerald : PG.red;
  const maintAccent = fleet.maintMTD > FLEET_GROUP.maintMTD / 21 * 1.1 ? PG.red : PG.amber;

  const watchlist = complianceWatchlist(vehicles, 60);

  const renderSortIndicator = (key) => {
    if (key !== sortKey) return null;
    return <span className="text-[#D71921] ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const yFmt = (v) => (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toString());

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-3 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-neutral-500">
            OPERATIONS / FLEET / BRANCH
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-2xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
              {branch.town} &middot; {branch.country} &middot; {fleet.size} vehicles
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone="info">Peer: {branch.peerGroup}</StatusPill>
          <StatusPill tone={healthTone}>Fleet_Health: {healthLabel}</StatusPill>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-3 flex-shrink-0">
        <KPICard
          title="Fleet_Size"
          value={fleet.size}
          unit="vehicles"
          target="-"
          icon={FiTruck}
          accent={PG.cyan}
        />
        <KPICard
          title="Avg_Util"
          value={`${fleet.avgUtilPct}%`}
          target={`${FLEET_GROUP.avgUtilPct}% group`}
          trend={fleet.avgUtilPct >= FLEET_GROUP.avgUtilPct ? 'up' : 'down'}
          trendValue={`${(fleet.avgUtilPct - FLEET_GROUP.avgUtilPct).toFixed(1)}%`}
          icon={FiActivity}
          accent={utilAccent}
        />
        <KPICard
          title="Km_MTD"
          value={(fleet.kmMTD / 1000).toFixed(1) + 'k'}
          unit="km"
          icon={FiMapPin}
          accent={PG.violet}
        />
        <KPICard
          title="Fuel_Cost_MTD"
          value={fmtRk(fleet.fuelMTD)}
          target={`${fleet.avgFuelEff} L/100km`}
          icon={FiDroplet}
          accent={PG.amber}
        />
        <KPICard
          title="Maint_Cost_MTD"
          value={fmtRk(fleet.maintMTD)}
          target={`${fmtRk(FLEET_GROUP.maintMTD / 21)} grp/br`}
          icon={FiTool}
          accent={maintAccent}
        />
        <KPICard
          title="Rev_/_Vehicle"
          value={fmtRk(fleet.revPerVehicle)}
          target={`${fmtRk(FLEET_GROUP.revPerVehicle)} group`}
          trend={fleet.revPerVehicle >= FLEET_GROUP.revPerVehicle ? 'up' : 'down'}
          trendValue={fleet.revPerVehicle >= FLEET_GROUP.revPerVehicle ? 'Above' : 'Below'}
          icon={FiDollarSign}
          accent={revAccent}
        />
        <KPICard
          title="Cost_/_Km"
          value={'R ' + fleet.costPerKm.toFixed(2)}
          target={`R ${FLEET_GROUP.costPerKm.toFixed(2)} group`}
          trend={fleet.costPerKm <= FLEET_GROUP.costPerKm ? 'up' : 'down'}
          trendValue={fleet.costPerKm <= FLEET_GROUP.costPerKm ? 'Efficient' : 'Over'}
          icon={FiTrendingDown}
          accent={cpkAccent}
        />
      </div>

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <Panel
          title="Vehicle_Roster"
          subtitle="click row to inspect // click header to sort"
          right={<StatusPill tone="ok">{fleet.active}/{fleet.size} Active</StatusPill>}
          className="col-span-7 flex-1 min-h-0"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                <tr className="border-b border-white/10">
                  {COLS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 cursor-pointer hover:text-white select-none ${c.align === 'right' ? 'text-right' : ''}`}
                    >
                      {c.label}{renderSortIndicator(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((v) => {
                  const isSel = v.id === selected?.id;
                  return (
                    <tr
                      key={v.id}
                      onClick={() => setSelectedId(v.id)}
                      className={`border-b border-white/5 transition-colors cursor-pointer ${isSel ? 'bg-[#D71921]/10 border-l-2 border-l-[#D71921]' : 'hover:bg-white/5'}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white">{v.id}</div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">{v.registration}</div>
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-300">
                        {TYPE_SHORT[v.typeId] || v.typeName}
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-300">{v.model}</td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{v.techAssigned}</td>
                      <td className="px-3 py-2">
                        <StatusPill tone={statusTone(v.status)}>{v.status}</StatusPill>
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-white">
                        {v.kmThisMonth.toLocaleString('en-ZA')}
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">{fmtRk(v.fuelCostMTD)}</td>
                      <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">{fmtRk(v.maintCostMTD)}</td>
                      <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">
                        {v.stockSalesMTD > 0 ? fmtRk(v.stockSalesMTD) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-doto text-base" style={{ color: utilColor(v.utilPct) }}>
                          {v.utilPct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="col-span-5 flex flex-col gap-3 min-h-0">
          <Panel title="Selected_Vehicle" className="flex-shrink-0">
            {selected && (
              <div className="p-3">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="font-doto text-2xl text-white leading-none">{selected.id}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{selected.registration}</div>
                  </div>
                  <StatusPill tone={statusTone(selected.status)}>{selected.status}</StatusPill>
                </div>
                <div className="font-mono-tech text-[11px] text-neutral-300 uppercase tracking-wider">{selected.make} {selected.model}</div>
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 mt-0.5">
                  {selected.techAssigned} &middot; {selected.year} &middot; {selected.ageYears}y
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Km_Lifetime</div>
                    <div className="font-doto text-base text-white mt-1">{fmtKm(selected.kmLifetime)}</div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Next_Service</div>
                    <div className={`font-doto text-base mt-1 ${selected.kmToService <= 0 ? 'text-[#D71921]' : 'text-white'}`}>
                      {selected.kmToService <= 0 ? 'OVERDUE' : `in ${selected.kmToService.toLocaleString('en-ZA')} km`}
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Licence_Expiry</div>
                    <div className={`font-doto text-base mt-1 ${isPast(selected.licenceExpiry) ? 'text-[#D71921]' : 'text-white'}`}>
                      {selected.licenceExpiry}
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Insurance_Expiry</div>
                    <div className={`font-doto text-base mt-1 ${isPast(selected.insuranceExpiry) ? 'text-[#D71921]' : 'text-white'}`}>
                      {selected.insuranceExpiry}
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Incidents_12m</div>
                    <div
                      className="font-doto text-base mt-1"
                      style={{ color: selected.incidents12m >= 2 ? PG.red : selected.incidents12m === 1 ? PG.amber : PG.emerald }}
                    >
                      {selected.incidents12m}
                    </div>
                  </div>
                  <div className="border border-white/5 p-2">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Fuel_Eff</div>
                    <div className="font-doto text-base text-white mt-1">{selected.fuelLPer100km} L/100km</div>
                  </div>
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Selected_Vehicle_12m_Trend" subtitle="km / fuel R / maint R" className="flex-1 min-h-0">
            <div className="h-full p-2">
              {selected && (
                <PGLineChart
                  categories={MONTHS_FLEET}
                  series={[
                    {
                      name: 'Km',
                      data: selected.trends.km.map((v) => +v.toFixed(0)),
                      lineStyle: { color: PG.violet, width: 2 },
                      itemStyle: { color: PG.violet },
                      areaStyle: { color: PG.violet, opacity: 0.08 },
                    },
                    {
                      name: 'Fuel R',
                      data: selected.trends.fuelCost.map((v) => +v.toFixed(0)),
                      lineStyle: { color: PG.amber, width: 2 },
                      itemStyle: { color: PG.amber },
                      areaStyle: undefined,
                    },
                    {
                      name: 'Maint R',
                      data: selected.trends.maintCost.map((v) => +v.toFixed(0)),
                      lineStyle: { color: PG.red, width: 2 },
                      itemStyle: { color: PG.red },
                      areaStyle: undefined,
                    },
                  ]}
                  yFormatter={yFmt}
                />
              )}
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 flex-shrink-0 h-48">
        <Panel title="Type_Mix" subtitle="vehicles by class" className="col-span-4">
          <div className="h-full p-2">
            <PGPieChart data={fleet.typeMix.filter((t) => t.count > 0).map((t) => ({ name: t.name, value: t.count }))} />
          </div>
        </Panel>

        <Panel
          title="Compliance_Flags"
          subtitle={`${watchlist.length} flagged // 60d horizon`}
          right={watchlist.length === 0 ? <StatusPill tone="ok">Clean</StatusPill> : <StatusPill tone="warn">{watchlist.length}</StatusPill>}
          className="col-span-8"
        >
          <div className="h-full overflow-y-auto custom-scrollbar p-2">
            {watchlist.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00C27A]">
                ALL_COMPLIANT
              </div>
            ) : (
              <div className="space-y-1">
                {watchlist.map((v) => (
                  <div
                    key={v.id}
                    onClick={() => setSelectedId(v.id)}
                    className="flex items-center justify-between px-2 py-1.5 border border-white/5 hover:bg-white/5 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate">{v.id}</div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                          {v.registration} &middot; {v.techAssigned}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      {v.flags.map((f, i) => (
                        <StatusPill key={i} tone={f.tone}>{f.label}</StatusPill>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
